<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-infinite-scroll ref="infiniteScroll" @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="tvsKundenListe"
                        :loading="loading"
                        :items-length="totalNumberOfRecords || 0"
                        :must-sort="true"
                        item-value="tvsKundennummer"
                        hover
                        show-expand
                        hide-default-footer
                        class="elevation-1 nowhow-table"
                        @update:sort-by="onSortByChanged"
                    >
                        <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                        <template #top>
                            <v-col sm="12" class="pb-0">
                                <v-row cols="auto">
                                    <v-col>
                                        <h3 class="pl-3" v-text="$t('lieferant.tvskundenliste.list.title')" />
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-tooltip location="bottom">
                                            <template #activator="{ props }">
                                                <v-btn
                                                    class="px-1 mr-2"
                                                    style="min-width: 160px"
                                                    color="primary"
                                                    v-bind="props"
                                                    @click="downloadTvsKundenListe"
                                                >
                                                    {{ $t('lieferant.tvskunden.button.download.csv') }}
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('lieferant.tvskunden.button.download.csv.tooltip') }}</span>
                                        </v-tooltip>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <TableHeader :filters="filters" @chip-closed="removeFilter">
                                <template #default>
                                    <div class="table-header-item-count">
                                        {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                    </div>
                                    <v-text-field
                                        v-if="showSearchBar"
                                        v-model="filterData.suchText"
                                        class="ml-5"
                                        prepend-inner-icon="mdi-magnify"
                                        append-inner-icon="mdi-close"
                                        :label="$t('suche')"
                                        hide-details
                                        autofocus
                                        @update:model-value="searchFieldInputChanged"
                                        @click:append-inner="searchBarClosed"
                                    ></v-text-field>
                                    <v-spacer />
                                    <v-tooltip v-if="!showSearchBar" top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary">
                                                <v-icon @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.search') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                                <v-icon>mdi-filter-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.filter') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                </template>
                            </TableHeader>
                        </template>
                        <template #item.tvsKundennummer="{ item }">
                            <TextHighlights :text="item.tvsKundennummer" :search-text="filterData.suchText" />
                        </template>
                        <template #item.name="{ item }">
                            <TextHighlights :text="item.name" :search-text="filterData.suchText" />
                        </template>
                        <template #item.vorname="{ item }">
                            <TextHighlights :text="item.vorname" :search-text="filterData.suchText" />
                        </template>
                        <template #item.firma="{ item }">
                            <TextHighlights :text="item.firma" :search-text="filterData.suchText" />
                        </template>
                        <template #item.plz="{ item }">
                            <TextHighlights :text="item.plz" :search-text="filterData.suchText" />
                        </template>
                        <template #item.ort="{ item }">
                            <TextHighlights :text="item.ort" :search-text="filterData.suchText" />
                        </template>
                        <!-- Lieferanten-Rechnungen via VETdata -->
                        <template #item.hatRechnungenNurViaVetdata="{ item }">
                            {{ $t(item.hatRechnungenNurViaVetdata ? 'filter.ja' : 'filter.nein') }}
                        </template>
                        <!-- Status -->
                        <template #item.status="{ item }">
                            <span v-if="item.geloescht && item.gesperrt">{{ $t('benutzerSuche.table.gesperrtundgeloescht') }}</span>
                            <span v-else-if="item.gesperrt">{{ $t('benutzerSuche.table.gesperrt') }}</span>
                            <span v-else-if="item.geloescht">{{ $t('benutzerSuche.table.geloescht') }}</span>
                            <span v-else>{{ $t('benutzerSuche.table.aktiv') }}</span>
                        </template>
                        <!-- Override default expand button -->
                        <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
                            <v-btn icon class="elevation-0" @click="toggleExpand(internalItem)">
                                <v-icon>{{ isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </v-btn>
                        </template>
                        <template #expanded-row="{ item }">
                            <td colspan="8" class="pr-5">
                                <div class="elevation-2 rounded-lg pa-4 ma-3 bg-surface-dark">
                                    <v-container fluid>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.name') }}</h5>
                                                <p>{{ item.anrede }} {{ item.vorname }} {{ item.name }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">
                                                    {{ $t('tvskundeliste.details.table.header.tvsKundennummer') }}
                                                </h5>
                                                <p>{{ item.tvsKundennummer }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">
                                                    {{ $t('tvskundeliste.details.table.header.hatRechnungenNurViaVetdata') }}
                                                </h5>
                                                <p>{{ $t(item.hatRechnungenNurViaVetdata ? 'filter.ja' : 'filter.nein') }}</p>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('taketeilnehmer.table.firma') }}</h5>
                                                <p>{{ item.firma }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.sprache') }}</h5>
                                                <p>{{ item.sprache?.toUpperCase() }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.gesperrt') }}</h5>
                                                <p>{{ $t(item.gesperrt ? 'filter.ja' : 'filter.nein') }}</p>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.strasse') }}</h5>
                                                <p>{{ item.strasse }} {{ item.hausNummer }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.telefon') }}</h5>
                                                <p>{{ item.telefon }}</p>
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.geloescht') }}</h5>
                                                <p>{{ $t(item.geloescht ? 'filter.ja' : 'filter.nein') }}</p>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">
                                                    {{ $t('tvskundeliste.details.table.header.plz') }} /
                                                    {{ $t('tvskundeliste.details.table.header.ort') }}
                                                </h5>
                                                <p>{{ item.plz }} {{ item.ort }}</p>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.zusPlz') }}</h5>
                                                <p>{{ item.zusPlz }}</p>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <h5 class="font-weight-bold">{{ $t('tvskundeliste.details.table.header.landcode') }}</h5>
                                                <p>{{ item.landcode }}</p>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </div>
                            </td>
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
        <!-- FilterMenü -->
        <FilterDrawer :visible="showFilterDrawer" @cancel="cancelFilter()" @confirm="confirmFilter()" @reset="resetFilter()">
            <SingleFilter :title="$t('taketeilnehmer.table.header.status')">
                <template #default>
                    <v-row>
                        <v-col sm="12">
                            <v-select v-model="filterData.status" filled dense hide-details :items="status"></v-select>
                        </v-col>
                    </v-row>
                </template>
            </SingleFilter>
            <SingleFilter :title="$t('taketeilnehmer.table.header.hatRechnungenNurViaVetdata')">
                <template #default>
                    <v-row>
                        <v-col sm="12">
                            <v-select v-model="filterData.hatRechnungenNurViaVetdata" filled dense hide-details :items="hatRechnungenNurViaVetdata"></v-select>
                        </v-col>
                    </v-row>
                </template>
            </SingleFilter>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { TvsKundeListApiDto } from '@/api-schema';

import { NAME as NAME_LIEFERANT } from '../store';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_GENERAL } from '@/store/modules/general';

const lieferantModul = namespace(NAME_LIEFERANT);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const generalModul = namespace(NAME_GENERAL);

import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { GET_TVS_KUNDEN_LISTE, GET_TVS_KUNDEN_LOADING, GET_TVS_KUNDEN_TOTAL_NUMBER_OF_RECORDS } from '../store/getters';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';

import { TeilnehmerFilterDto } from '@/types/TeilnehmerFilterDto';
import TableHeader from '@/components/TableHeader.vue';
import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import { APPLY_TVS_KUNDEN_FILTER, CLEAR_TVS_KUNDEN_FILTER } from '../store/mutations';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';
import { DOWNLOAD_TVS_KUNDEN_LISTE, GET_NEXT_TVS_KUNDE, LOAD_TVS_KUNDEN } from '@/areas/lieferant/store/actions';

@Component({
    components: {
        TableHeader,
        SingleFilter,
        FilterDrawer,
        TextHighlights
    }
})
export default class TvsKundenListe extends Vue {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @lieferantModul.Action(LOAD_TVS_KUNDEN) loadTvsKunden!: Function;
    @lieferantModul.Action(GET_NEXT_TVS_KUNDE) getNextTvsKunden!: Function;
    @lieferantModul.Action(DOWNLOAD_TVS_KUNDEN_LISTE)
    downloadTvsKundenListe!: Function;
    @lieferantModul.Getter(GET_TVS_KUNDEN_LISTE)
    tvsKundenListe!: Array<TvsKundeListApiDto>;
    @lieferantModul.Getter(GET_TVS_KUNDEN_LOADING) loading!: boolean;
    @lieferantModul.Getter(GET_TVS_KUNDEN_TOTAL_NUMBER_OF_RECORDS)
    totalNumberOfRecords!: number;
    @lieferantModul.Mutation(CLEAR_TVS_KUNDEN_FILTER) clearFilter!: Function;
    @lieferantModul.Mutation(APPLY_TVS_KUNDEN_FILTER) applyFilters!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    filters: Array<TableHeaderFilterItem> = [];
    showSearchBar = true;
    showFilterDrawer = false;
    infiniteLoad = null;

    sortBy: [{ key: 'tvsKundennummer'; order: 'desc' }];

    filterData: TeilnehmerFilterDto = new TeilnehmerFilterDto();
    filterDataCopy: TeilnehmerFilterDto = new TeilnehmerFilterDto();

    get status(): Array<any> {
        return [
            {
                title: this.$t('taketeilnehmer.status.aktiv'),
                value: 'taketeilnehmer.status.aktiv'
            },
            {
                title: this.$t('taketeilnehmer.status.gesperrt'),
                value: 'taketeilnehmer.status.gesperrt'
            },
            {
                title: this.$t('taketeilnehmer.status.geloescht'),
                value: 'taketeilnehmer.status.geloescht'
            }
        ];
    }

    get hatRechnungenNurViaVetdata(): Array<any> {
        return [
            { title: this.$t('filter.ja'), value: true },
            { title: this.$t('filter.nein'), value: false }
        ];
    }

    created() {
        this.setSubTitle(this.$t('lieferant.tvskundenliste.title'));
        this.setFooterTitle('S510');
        if (!this.hasAccess('Navigation.TvsKundenListe')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
        }

        this.filterData.status = 'taketeilnehmer.status.aktiv';
        this.applyFilters(this.filterData);
        this.loadTvsKunden();
        this.showFilterDrawerChanged(true);
    }

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;
    mounted() {
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.loadTvsKunden();
        }, 400);
    }

    get headers(): Array<any> {
        return [
            {
                title: this.$t('table.header.tvs.nr'),
                key: 'tvsKundennummer'
            },
            {
                title: this.$t('einstellungen.benutzer.table.name'),
                key: 'name'
            },
            {
                title: this.$t('einstellungen.benutzer.table.vorname'),
                key: 'vorname'
            },
            {
                title: this.$t('taketeilnehmer.table.firma'),
                key: 'firma'
            },
            {
                title: this.$t('benutzerSuche.table.plz'),
                key: 'plz'
            },
            {
                title: this.$t('benutzerSuche.table.ort'),
                key: 'ort'
            },
            {
                title: this.$t('taketeilnehmer.table.header.hatRechnungenNurViaVetdata'),
                key: 'hatRechnungenNurViaVetdata'
            },
            {
                title: this.$t('taketeilnehmer.table.header.status'),
                key: 'status',
                sortable: false
            }
        ];
    }
    get subHeaders(): Array<any> {
        return [
            {
                title: this.$t('table.header.tvs.nr'),
                key: 'tvsKundennummer'
            },
            {
                title: this.$t('einstellungen.benutzer.anrede'),
                key: 'anrede'
            },
            {
                title: this.$t('einstellungen.benutzer.table.name'),
                key: 'name'
            },
            {
                title: this.$t('einstellungen.benutzer.table.vorname'),
                key: 'vorname'
            },
            {
                title: this.$t('taketeilnehmer.table.firma'),
                key: 'firma'
            },
            {
                title: this.$t('benutzerSuche.table.strasse'),
                key: 'strasse'
            },
            {
                title: this.$t('benutzerSuche.table.strasseNummer'),
                key: 'hausNummer'
            },
            {
                title: this.$t('benutzerSuche.table.plz'),
                key: 'plz'
            },
            {
                title: this.$t('benutzerSuche.table.ort'),
                key: 'ort'
            },
            {
                title: this.$t('benutzerSuche.table.landcode'),
                key: 'landcode'
            },
            {
                title: this.$t('benutzerSuche.table.sprache'),
                key: 'sprache'
            },
            {
                title: this.$t('benutzerSuche.table.zusPlz'),
                key: 'zusPlz'
            },
            {
                title: this.$t('benutzerSuche.table.geloescht'),
                key: 'geloescht'
            },
            {
                title: this.$t('benutzerSuche.table.gesperrt'),
                key: 'gesperrt'
            },
            {
                title: this.$t('taketeilnehmer.table.header.hatRechnungenNurViaVetdata'),
                key: 'hatRechnungenNurViaVetdata'
            },
            {
                title: this.$t('benutzerSuche.table.telefon'),
                key: 'telefon'
            }
        ];
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.suchText = '';
        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.showFilterDrawer = false;
        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    private resetFilter() {
        this.filterData = new TeilnehmerFilterDto();
        this.filterData.status = 'taketeilnehmer.status.aktiv';
        this.applyFilters(this.filterData);
    }

    private confirmFilter() {
        this.showFilterDrawer = false;
        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    async load({ done }) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            // initialer datenload abwarten, sonst wird fälschlicherweise 'empty' emitted
            done('ok');
            return;
        }

        this.applyFilters(this.filterData);
        await this.getNextTvsKunden();

        if (this.tvsKundenListe.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    private removeFilter(value: string) {
        this.filters = this.filters.filter(function (obj) {
            return obj.value !== value;
        });

        if (value === 'taketeilnehmer.table.header.status') {
            this.filterData.status = '';
        }
        if (value === 'taketeilnehmer.table.header.hatRechnungenNurViaVetdata') {
            this.filterData.hatRechnungenNurViaVetdata = undefined;
        }

        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    private customSort(items: any[]) {
        return items;
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0];

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }

        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }

    @Watch('showFilterDrawer')
    showFilterDrawerChanged(newVal) {
        if (newVal) {
            this.filterDataCopy = Object.assign({}, this.filterData);
        }
        this.filters = [];
        if (this.filterData.status !== '') {
            this.filters = this.filters.concat({
                key: 'taketeilnehmer.table.header.status',
                nameResourceKey: 'taketeilnehmer.table.header.status',
                value: this.$t(this.filterData.status).toString()
            });
        }
        if (this.filterData.hatRechnungenNurViaVetdata !== undefined) {
            this.filters = this.filters.concat({
                key: 'taketeilnehmer.table.header.hatRechnungenNurViaVetdata',
                nameResourceKey: 'taketeilnehmer.table.header.hatRechnungenNurViaVetdata',
                value: this.$t(this.filterData.hatRechnungenNurViaVetdata ? 'filter.ja' : 'filter.nein').toString()
            });
        }
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
